/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { CloseIcon, MagnifyIcon } from '@atgfr8/icons';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { get } from 'lodash';
import {
  createEffect,
  createSignal,
  For,
  JSX,
  mergeProps,
  onCleanup,
  onMount,
  Setter,
  untrack,
} from 'solid-js';

import ClickAwayListener from './ClickAwayListener';
import { AutocompleteProps } from './types';
import { useAutocomplete } from './useAutocomplete';

const defaultValue = {
  debounceDelay: 500,
  debounce: true,
  defaultValue: '',
  error: null,
  forceAutoSelect: false,
  mode: 'single',
  isStatic: false,
  minCharacters: 2,
};

/**
 * This autocomplete component is a drop-in replacement for the original autocomplete component
 * that can be found in this folder. It uses a different approach on how the requests are filled
 * and how the data is passed back and forth. It uses the `useAutocomplete` hook, which in turn
 * uses `useDebounceRequest`.
 * @param props
 * @returns
 */
export const DebounceAutoComplete = <T,>(props: AutocompleteProps<T>) => {
  const mp = mergeProps(defaultValue, props);
  const sanitizeInputValue = (value: string) =>
    mp.inputSanitizer ? mp.inputSanitizer(value) : value;
  const getResolvedInputValue = () =>
    sanitizeInputValue(
      props.value !== undefined ? props.value : (mp.defaultValue as string),
    );

  const [anchorEl, setAnchorEl] = createSignal<HTMLInputElement | undefined>();
  const [isDropdownOpen, setDropdownOpen] = createSignal<boolean>(false);
  const [activeIndex, setActiveIndex] = createSignal(-1);
  const [inputValue, setInputValue] = createSignal<string>(
    getResolvedInputValue(),
  );

  let listItemRef: HTMLLIElement[] = [];
  let observer: IntersectionObserver;
  let inputHasFocus = false;
  let pendingSelection: { start: number; end: number } | null = null;
  let pendingSelectionTarget: HTMLInputElement | null = null;
  let lastSyncedValue = getResolvedInputValue();
  let userInitiatedSearch = false;

  const restorePendingSelection = (clearPending = false) => {
    if (!pendingSelection || !pendingSelectionTarget) {
      return;
    }

    const target = pendingSelectionTarget;
    if (document.activeElement !== target) {
      return;
    }

    const maxLength = target.value.length;
    const start = Math.min(pendingSelection.start, maxLength);
    const end = Math.min(pendingSelection.end, maxLength);
    target.setSelectionRange(start, end);

    if (clearPending) {
      pendingSelection = null;
      pendingSelectionTarget = null;
    }
  };

  onMount(() => {
    const initialValue = getResolvedInputValue();
    setSearchTerm(initialValue);
    setInputValue(initialValue);

    const textFieldElement = document.getElementById(
      props.id,
    ) as HTMLInputElement;

    if (Boolean(textFieldElement)) {
      observer = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (Boolean(!entry.isIntersecting)) {
              setDropdownOpen(false);
            }
          });
        },
        {
          root: null,
          threshold: 0.1,
        },
      );

      observer.observe(textFieldElement);
    }

    if (Boolean(props.isStatic)) {
      setDropdownOpen(true);
      props.staticItemList && setSearchResults(props.staticItemList);
    }
  });

  onCleanup(() => {
    if (Boolean(observer)) {
      observer.disconnect();
    }
  });

  const handleSelect = (item: T, isProgrammatic = false) => {
    // Intent is consumed once a result is selected.
    userInitiatedSearch = false;

    if (props.shouldPerformSearch ?? false) {
      setSearchTerm('');
      setInputValue('');
    }

    setDropdownOpen(false);

    if (props.onItemSelect) {
      props.onItemSelect(item, isProgrammatic);
    }

    setActiveIndex(-1);

    if (Boolean(props.clearSearch)) {
      handleClearSearch();
    } else {
      if (props.valueName !== undefined) {
        const locationValue = get(item, props.valueName) ?? '';
        const resolvedLocationValue = sanitizeInputValue(
          typeof locationValue === 'string'
            ? locationValue
            : String(locationValue),
        );
        setSearchTerm(resolvedLocationValue);
        setInputValue(resolvedLocationValue);
      }
    }
  };

  const {
    searchTerm,
    setSearchTerm,
    results: searchResults,
    setResults: setSearchResults,
    isLoading,
  } = useAutocomplete<T[]>({
    endpoint: props.endpoint,
    useV3Client: props.useV3Client,
    version: props.version,
    minCharacters: 2,
  });

  // Report result-count changes so parents can tell "no matches returned"
  // apart from "matches shown but none selected". Only notify on an actual
  // count change, and untrack the callback so anything it reads (e.g. a form
  // store written by setFields) never becomes a dependency of this effect —
  // otherwise form writes would re-trigger it in a synchronous update loop.
  //
  let lastReportedResultCount = -1;
  createEffect(() => {
    const results = searchResults();
    if (results === null) {
      lastReportedResultCount = -1;
      return;
    }
    const count = results.length;
    if (count === lastReportedResultCount) {
      return;
    }
    lastReportedResultCount = count;
    const notifyResultsChange: ((count: number) => void) | undefined =
      props.onResultsChange;
    if (typeof notifyResultsChange === 'function') {
      untrack(() => notifyResultsChange(count));
    }
  });

  const handleInputFocus = (event: MouseEvent) => {
    const results = searchResults() ?? [];
    const hasSearch = Boolean(inputValue());
    const resultsCount = results.length;

    // A single result is auto-selected by the search effect, never offered as a
    // one-item dropdown. Only multi-match results reopen the dropdown on focus.
    const isLoneAutoSelect =
      resultsCount === 1 && Boolean(props.forceAutoSelect);

    if (hasSearch && resultsCount > 0 && !isLoneAutoSelect) {
      // Keep focus behavior passive: opening a field to edit should never
      // trigger a programmatic selection that can move the caret.
      setDropdownOpen(true);
    }

    event.stopPropagation();
    inputHasFocus = true;
  };

  const handleClearSearch = () => {
    userInitiatedSearch = false;
    setSearchTerm('');
    setInputValue('');
    setDropdownOpen(false);

    if (props.onClearValue) {
      props.onClearValue();
      setSearchResults([]);
    }

    setActiveIndex(-1);
  };

  function renderEndAdornment() {
    if (isLoading()) {
      return <CircularProgress class="p-2!" />;
    } else if (searchTerm() && !Boolean(props.disabled)) {
      return (
        <CloseIcon
          style={{ cursor: 'pointer', 'z-index': '49' }}
          onClick={handleClearSearch}
        />
      );
    } else if (Boolean(props.hideIcon) || Boolean(props.disabled)) {
      return <></>;
    }

    return <MagnifyIcon style={{ cursor: 'pointer' }} />;
  }

  const handleInputChange = (e: Event) => {
    // Typing implies focus is active even if a wrapper focus event is missed.
    inputHasFocus = true;
    userInitiatedSearch = true;

    // NOTE: set activeIndex to 0 and reset listItemref when search query changes
    // Results are set to `null` (pending), not `[]`, so the result-count
    // reporter above does not fire mid-keystroke.
    listItemRef = [];
    setSearchResults(null);
    setActiveIndex(0);
    const target = e.target as HTMLInputElement;

    if (props.phoneInput ?? false) {
      const cursorPosition = target.selectionStart;
      const oldValue = target.value;
      const oldLength = oldValue.length;
      const newUnformattedValue = oldValue.replace(/[^0-9]/g, '');

      if (newUnformattedValue.length <= 20) {
        const newValue = formatPhoneNumber(newUnformattedValue);
        const newLength = newValue.length;
        setSearchTerm(newValue);
        setInputValue(newValue);
        mp.onChange && mp.onChange(newUnformattedValue);

        if (newValue.length === 0) {
          setDropdownOpen(false);
        }

        if (cursorPosition !== null) {
          const diff = newLength - oldLength;

          target.setSelectionRange(
            cursorPosition + diff,
            cursorPosition + diff,
          );
        }
      }

      // void performSearch(newUnformattedValue);
    } else {
      const sanitizedValue = sanitizeInputValue(target.value);
      if (sanitizedValue !== target.value) {
        target.value = sanitizedValue;
      }
      const selectionStart = target.selectionStart;
      const selectionEnd = target.selectionEnd;

      // Keep the controlled value in sync with raw typing.
      setInputValue(sanitizedValue);
      setSearchTerm(sanitizedValue);
      mp.onChange && mp.onChange(sanitizedValue);
      // void performSearch(target.value);

      if (selectionStart !== null && selectionEnd !== null) {
        pendingSelection = { start: selectionStart, end: selectionEnd };
        pendingSelectionTarget = target;
        queueMicrotask(() => restorePendingSelection(false));
        requestAnimationFrame(() => restorePendingSelection(false));
        setTimeout(() => restorePendingSelection(true), 0);
      }

      if (sanitizedValue.length === 0) {
        setDropdownOpen(false);
      }
    }
  };

  const handleTextFieldInput = (e: Event) => {
    handleInputChange(e);
    props.onInput?.(e);
  };

  const handleEnterSelect = () => {
    if (
      isNaN(activeIndex()) ||
      activeIndex() < 0 ||
      activeIndex() >= (searchResults() ?? []).length
    ) {
      return;
    }

    handleSelect((searchResults() ?? [])[activeIndex()], true);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    //NOTE: Removed create effect causing issues with multiple triggers and used event callback from TextField
    if (e.key === 'Tab') {
      setDropdownOpen(false);
      setActiveIndex(-1);
      inputHasFocus = false;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      handleEnterSelect();
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if ((searchResults() ?? []).length > 0 && isDropdownOpen()) {
        setActiveIndex((prevIndex) => {
          if (e.key === 'ArrowUp') {
            if (prevIndex === -1 || prevIndex === 0) {
              return (searchResults() ?? []).length - 1;
            }

            return prevIndex - 1;
          }

          return (prevIndex + 1) % (searchResults() ?? []).length;
        });

        if (activeIndex() >= 0 && activeIndex() < listItemRef.length) {
          const activeElement = listItemRef[activeIndex()];
          activeElement.scrollIntoView({
            block: 'nearest',
          });
        }
      }
    }
  };

  createEffect(() => {
    inputValue();

    if (!pendingSelection || !pendingSelectionTarget) {
      return;
    }

    queueMicrotask(() => restorePendingSelection(false));
    requestAnimationFrame(() => restorePendingSelection(false));
    setTimeout(() => restorePendingSelection(true), 0);
  });

  createEffect(() => {
    const results = searchResults() ?? [];
    const resultsCount = results.length;
    const shouldAutoSelect = props.forceAutoSelect;
    const inputEl = anchorEl();
    const caretPosition = inputEl?.selectionStart;
    const isEditingMiddle =
      Boolean(inputEl) &&
      document.activeElement === inputEl &&
      caretPosition !== null &&
      caretPosition !== undefined &&
      caretPosition < inputValue().length;

    // A search the user started by typing must still resolve after they tab
    // out (userInitiatedSearch). Prefill/programmatic searches never set that
    // flag, so they stay suppressed unless the field is focused.
    if (!inputHasFocus && !userInitiatedSearch) {
      return;
    }

    if (resultsCount === 1 && shouldAutoSelect && !isEditingMiddle) {
      handleSelect(results[0]);
    } else if (resultsCount > 0) {
      if (inputHasFocus) {
        setDropdownOpen(true);
      }
    } else if (resultsCount === 0 && searchTerm().length === 0) {
      setDropdownOpen(false);
    }
  });

  createEffect(() => {
    const nextValue = getResolvedInputValue();
    const currentInputValue = inputValue();
    const hasDomFocus = document.activeElement === anchorEl();
    const isActivelyEditing = inputHasFocus || hasDomFocus;

    if (nextValue === lastSyncedValue) {
      return;
    }

    lastSyncedValue = nextValue;

    // Avoid no-op writes that can reset caret position in controlled inputs.
    if (nextValue === currentInputValue) {
      return;
    }

    // While the user has the field focused (mid-edit), only apply explicit
    // clears from the parent. Any other change (e.g. an auto-selection that
    // normalised the value, or a stale API response) must not clobber the
    // user's in-progress typing or jump their cursor.
    if (isActivelyEditing && nextValue !== '') {
      return;
    }

    // A programmatic value change (prefill, customer/order load, clear)
    // supersedes any pending user-initiated search intent.
    userInitiatedSearch = false;

    setSearchTerm(nextValue);
    setInputValue(nextValue);
  });

  const getMenuWidth = () => {
    const input = anchorEl();
    const fieldShell = input?.closest('label.input') as HTMLElement | null;
    // Use exact offsetWidth which includes border
    return fieldShell?.offsetWidth ?? input?.offsetWidth;
  };

  const getMenuLeftOffset = () => {
    const input = anchorEl();
    const fieldShell = input?.closest('label.input') as HTMLElement | null;

    if (!input || !fieldShell) {
      return 0;
    }

    const offset =
      input.getBoundingClientRect().left -
      fieldShell.getBoundingClientRect().left;

    return Math.max(0, offset);
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        const el = anchorEl();
        if (el) {
          const sanitizedValue = sanitizeInputValue(el.value);
          if (sanitizedValue !== el.value) {
            el.value = sanitizedValue;
          }
          setInputValue(sanitizedValue);
          setSearchTerm(sanitizedValue);
        }
        setDropdownOpen(false);
        setActiveIndex(-1);
        inputHasFocus = false;
        userInitiatedSearch = false;
        pendingSelection = null;
        pendingSelectionTarget = null;
      }}
      innerRefClass={mp.innerRefClass}
    >
      <TextField
        {...((props.inputProps ?? {}) as Record<string, unknown>)}
        ref={setAnchorEl as Setter<HTMLInputElement | undefined>}
        {...({
          'on:blur': () => {
            const el = anchorEl();
            if (el) {
              const sanitizedValue = sanitizeInputValue(el.value);
              if (sanitizedValue !== el.value) {
                el.value = sanitizedValue;
              }
              setInputValue(sanitizedValue);
              setSearchTerm(sanitizedValue);
            }
            inputHasFocus = false;
            pendingSelection = null;
            pendingSelectionTarget = null;
            props.onBlur?.();
          },
          'on:focus': (e: unknown) => handleInputFocus(e as MouseEvent),
          'on:input': handleTextFieldInput,
        } as Record<string, unknown>)}
        name={props.name}
        id={props.id}
        fullWidth
        required={props.required}
        autocomplete="off"
        value={inputValue()}
        placeholder={props.placeholder}
        class={props.class}
        label={props.label}
        startIcon={props.startAdornment}
        endIcon={
          props.disableClear !== true
            ? ((props.customButton?.(isLoading()) as JSX.Element) ??
              renderEndAdornment())
            : undefined
        }
        onKeyDown={handleKeyDown}
        size={
          props.size === 'medium'
            ? 'md'
            : props.size === 'small'
              ? 'sm'
              : props.size
        }
        dataTestId={props.dataTestId ?? ''}
        error={Boolean(props.disabled) ? undefined : Boolean(props.error)}
        disabled={props.disabled}
        helperText={
          Array.isArray(props.error) && props.error.length > 0
            ? props.error[0]
            : ''
        }
      />
      {props.multipleValueRender &&
        props.mode === 'multiple' &&
        props.multipleValueRender()}
      {props.multipleSelectValues &&
        props.mode === 'multiple' &&
        props.multipleSelectValues.map((value, index) => (
          <Chip
            id={`chip-${props.id}-${index}`}
            text={<>{get(value, `${props.renderMultipleKey}`)}</>}
            isDeleteIcon={true}
            deleteIconColor="white"
            backgroundColor="#026EA1"
            class="my-1 mr-1 text-white rounded"
            onDelete={() => {
              props.onMultipleDelete && props.onMultipleDelete(value);
              handleClearSearch();
            }}
          />
        ))}
      <Menu
        anchorEl={anchorEl}
        class={
          Boolean(props.listClass)
            ? `${props.listClass}`
            : `overflow-auto! max-h-60! ${
                (searchResults() ?? []).length > 0 ? '' : 'p-0!'
              }`
        }
        style={{
          display: isDropdownOpen() ? 'block' : 'none',
          width: (() => {
            const menuWidth = getMenuWidth();
            return menuWidth ? `${menuWidth}px` : undefined;
          })(),
          'margin-left': (() => {
            const leftOffset = getMenuLeftOffset();
            return leftOffset !== 0 ? `-${leftOffset}px` : undefined;
          })(),
        }}
      >
        {(searchResults() ?? []).length === 0 &&
          searchTerm().length > 3 &&
          !isLoading() && (
            <MenuItem class="hover:bg-gray-100">
              <div class="flex flex-col">
                <div>No results found</div>
                <div class="text-xs opacity-60">
                  Please try with a different name
                </div>
              </div>
            </MenuItem>
          )}
        <For each={searchResults() ?? []}>
          {(item: T, i) => (
            <MenuItem
              onClick={() => handleSelect(item)}
              class={`hover:bg-gray-100 cursor-pointer ${
                activeIndex() === i() ? 'bg-gray-100' : ''
              }`}
              style={props.renderItemStyle ? props.renderItemStyle : {}}
              ref={(el: HTMLLIElement) => {
                listItemRef[i()] = el;
              }}
            >
              {props.renderItem ? (
                props.renderItem(item)
              ) : (
                <span class="ml-5">
                  {get(item, props.responseFieldName ?? 'name')}
                </span>
              )}
            </MenuItem>
          )}
        </For>
      </Menu>
    </ClickAwayListener>
  );
};
